import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useState, } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react-dom';
import { useOutsideClick } from './useOutsideClick';
import { useDidMount } from './useDidMount';
export const Portal = forwardRef((props, ref) => {
    const didMount = useDidMount();
    const [shouldCalculate, setShouldCalculate] = useState(typeof props.shouldCalculate === 'undefined');
    useEffect(() => {
        if (typeof props.shouldCalculate === 'undefined') {
            return;
        }
        if (props.shouldCalculate?.is === true) {
            setShouldCalculate(true);
            return;
        }
        const timer = setTimeout(() => {
            setShouldCalculate(props.shouldCalculate?.is ?? false);
        }, props.shouldCalculate?.timeout ?? 0);
        return () => clearTimeout(timer);
    }, [props.shouldCalculate]);
    const { x, y, update, strategy, placement, refs } = useFloating({
        placement: props.placement ?? 'bottom-start',
        middleware: [
            offset({
                mainAxis: props.offsetMain ?? 0,
                crossAxis: props.offsetCross ?? 0,
            }),
            flip({
                mainAxis: !props.restrictFlipMain,
                crossAxis: !props.restrictFlipCross,
            }),
        ],
        open: shouldCalculate,
    });
    const { setFloating, setReference } = refs;
    useLayoutEffect(() => {
        if (props.reference?.current && shouldCalculate) {
            setReference(props.reference.current);
        }
    }, [props.reference, shouldCalculate]);
    useImperativeHandle(ref, () => ({
        update,
    }), []);
    useEffect(() => {
        if (shouldCalculate) {
            update();
            if (props.onPlacementChange) {
                props.onPlacementChange(placement);
            }
        }
    }, [placement, shouldCalculate]);
    useEffect(() => {
        update();
    }, [placement]);
    useEffect(() => {
        if (!refs.reference.current || !refs.floating.current || !didMount || !shouldCalculate) {
            return;
        }
        return autoUpdate(refs.reference.current, refs.floating.current, update);
    }, [didMount, shouldCalculate]);
    useOutsideClick(refs.floating, (event) => {
        if (props.onOutClick && shouldCalculate) {
            props.onOutClick(event);
        }
    });
    const styles = {
        top: '0',
        left: '0',
        zIndex: props.zIndex ?? 5,
        transform: shouldCalculate
            ? `translate3d(${Math.round(x ?? 0)}px,${Math.round(y ?? 0)}px, 0)`
            : 'translate3d(0,0,0)',
        minWidth: props.fullWidth ? refs.reference?.current?.offsetWidth : 0,
        pointerEvents: props.pointerEventsNone ? 'none' : 'auto',
    };
    const content = (React.createElement("div", { ref: setFloating, style: {
            position: props.renderInside ? 'absolute' : strategy,
            ...styles,
        } }, props.children));
    if (props.renderInside) {
        return content;
    }
    return createPortal(content, document.body);
});
//# sourceMappingURL=Portal.js.map